import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  getCleanupPreview, 
  cleanupSoftDeletedPlayer, 
  getAllSoftDeletedPlayers
} from '../services/anonymization';
import './SoftDeletedPlayerCleanup.css';

interface SoftDeletedPlayerCleanupProps {
  onCleanupComplete?: () => void;
}

const SoftDeletedPlayerCleanup: React.FC<SoftDeletedPlayerCleanupProps> = ({ onCleanupComplete }) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [allPlayers, setAllPlayers] = useState<any[]>([]);
  const [selectedPlayer, setSelectedPlayer] = useState<any>(null);
  const [preview, setPreview] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCleaning, setIsCleaning] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Load all soft-deleted players on mount
  useEffect(() => {
    loadAllPlayers();
  }, []);

  const loadAllPlayers = async () => {
    try {
      setIsLoading(true);
      const players = await getAllSoftDeletedPlayers();
      setAllPlayers(players);
    } catch (err) {
      console.error('Load error:', err);
      setError(t('cleanup.loadError'));
    } finally {
      setIsLoading(false);
    }
  };

  // Filter players based on search term
  const filteredPlayers = useMemo(() => {
    if (!searchTerm.trim()) return allPlayers;
    
    const term = searchTerm.toLowerCase();
    return allPlayers.filter(player => 
      player.name?.toLowerCase().includes(term) ||
      player.preName?.toLowerCase().includes(term) ||
      player.email?.toLowerCase().includes(term)
    );
  }, [allPlayers, searchTerm]);

  const loadPreview = useCallback(async () => {
    if (!selectedPlayer) return;

    try {
      setIsLoading(true);
      setError(null);
      const previewData = await getCleanupPreview(selectedPlayer.id);
      setPreview(previewData);
    } catch (err) {
      console.error('Preview error:', err);
      setError(t('cleanup.previewError'));
    } finally {
      setIsLoading(false);
    }
  }, [selectedPlayer, t]);

  // Load preview when player is selected
  useEffect(() => {
    if (selectedPlayer) {
      loadPreview();
    }
  }, [selectedPlayer, loadPreview]);

  const handlePlayerSelect = (player: any) => {
    setSelectedPlayer(player);
    setShowConfirm(false);
    setConfirmText('');
    setError(null);
    setSuccess(null);
  };

  const handleCleanup = async () => {
    if (!selectedPlayer || !preview?.success) return;

    try {
      setIsCleaning(true);
      setError(null);
      
      const result = await cleanupSoftDeletedPlayer(selectedPlayer.id);
      
      if (result.success) {
        setSuccess(t('cleanup.success', { 
          originalName: result.original_name,
          gamesAffected: result.games_affected 
        }));
        
        // Reset form
        setSelectedPlayer(null);
        setPreview(null);
        setShowConfirm(false);
        setConfirmText('');
        
        // Reload the list
        await loadAllPlayers();
        
        // Notify parent component
        if (onCleanupComplete) {
          onCleanupComplete();
        }
      } else {
        setError(result.error || t('cleanup.cleanupError'));
      }
    } catch (err) {
      console.error('Cleanup error:', err);
      setError(t('cleanup.cleanupError'));
    } finally {
      setIsCleaning(false);
    }
  };

  const canConfirm = confirmText === 'DELETE' && selectedPlayer && preview?.success;

  return (
    <div className="soft-deleted-player-cleanup">
      <div className="cleanup-header">
        <h3>🗑️ {t('cleanup.title')}</h3>
        <p className="warning-text">{t('cleanup.warning')}</p>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {success && (
        <div className="success-message">
          {success}
        </div>
      )}

      <div className="players-list-section">
        <div className="list-header">
          <h4>{t('cleanup.deletedPlayersList')} ({allPlayers.length})</h4>
          <input
            type="text"
            placeholder={t('cleanup.filterPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            disabled={isLoading || isCleaning}
            className="filter-input"
          />
        </div>
        
        {isLoading && <div className="loading">{t('common.loading')}</div>}
        
        {!isLoading && filteredPlayers.length === 0 && (
          <div className="no-players">
            {allPlayers.length === 0 
              ? t('cleanup.noDeletedPlayers')
              : t('cleanup.noMatchingPlayers')}
          </div>
        )}
        
        {!isLoading && filteredPlayers.length > 0 && (
          <div className="players-table">
            <table>
              <thead>
                <tr>
                  <th>{t('cleanup.name')}</th>
                  <th>{t('cleanup.email')}</th>
                  <th>{t('cleanup.deletedDate')}</th>
                  <th>{t('cleanup.type')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredPlayers.map((player) => (
                  <tr 
                    key={player.id}
                    className={selectedPlayer?.id === player.id ? 'selected' : ''}
                  >
                    <td className="player-name-cell">
                      {player.preName} {player.name}
                    </td>
                    <td className="player-email-cell">{player.email}</td>
                    <td className="deleted-date-cell">
                      {new Date(player.deleted_at).toLocaleDateString()}
                    </td>
                    <td className="type-cell">
                      {player.is_guest && <span className="guest-badge">Guest</span>}
                      {!player.is_guest && <span className="regular-badge">User</span>}
                    </td>
                    <td className="action-cell">
                      <button
                        className="select-btn"
                        onClick={() => handlePlayerSelect(player)}
                        disabled={isCleaning}
                      >
                        {selectedPlayer?.id === player.id 
                          ? t('cleanup.selected') 
                          : t('cleanup.select')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedPlayer && (
        <div className="selected-player">
          <h4>{t('cleanup.selectedPlayer')}</h4>
          <div className="player-info">
            <div className="player-name">
              {selectedPlayer.preName} {selectedPlayer.name}
              {selectedPlayer.is_guest && <span className="guest-badge">Guest</span>}
            </div>
            <div className="player-email">{selectedPlayer.email}</div>
            <div className="deleted-info">
              Deleted: {new Date(selectedPlayer.deleted_at).toLocaleDateString()}
            </div>
            <button
              className="change-player-btn"
              onClick={() => {
                setSelectedPlayer(null);
                setPreview(null);
                setSearchTerm('');
              }}
            >
              {t('cleanup.changePlayer')}
            </button>
          </div>
        </div>
      )}

      {preview && (
        <div className="preview-section">
          <h4>{t('cleanup.impactPreview')}</h4>
          
          {preview.success ? (
            <div className="preview-content">
              <div className="impact-summary">
                <div className="impact-item">
                  <span className="impact-label">{t('cleanup.gamesAffected')}</span>
                  <span className="impact-value">{preview.games_affected}</span>
                </div>
                <div className="impact-item">
                  <span className="impact-label">{t('cleanup.clubsAffected')}</span>
                  <span className="impact-value">{preview.clubs_affected}</span>
                </div>
                <div className="impact-item">
                  <span className="impact-label">{t('cleanup.licensesAffected')}</span>
                  <span className="impact-value">{preview.licenses_affected}</span>
                </div>
                <div className="impact-item">
                  <span className="impact-label">{t('cleanup.invitationsAffected')}</span>
                  <span className="impact-value">{preview.invitations_affected}</span>
                </div>
              </div>

              {preview.recent_games && preview.recent_games.length > 0 && (
                <div className="recent-games">
                  <h5>{t('cleanup.recentGames')}</h5>
                  <div className="games-list">
                    {preview.recent_games.map((game: any) => (
                      <div key={game.id} className="game-item">
                        <span className="game-players">
                          {game.player_a} vs {game.player_b}
                        </span>
                        <span className="game-date">
                          {new Date(game.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="cleanup-info">
                <h5>{t('cleanup.cleanupProcess')}</h5>
                <ol>
                  <li>{t('cleanup.step1')}</li>
                  <li>{t('cleanup.step2')}</li>
                  <li>{t('cleanup.step3')}</li>
                  <li>{t('cleanup.step4')}</li>
                  <li>{t('cleanup.step5')}</li>
                </ol>
              </div>

              {!showConfirm ? (
                <button
                  className="cleanup-btn"
                  onClick={() => setShowConfirm(true)}
                  disabled={isCleaning}
                >
                  {t('cleanup.proceedToCleanup')}
                </button>
              ) : (
                <div className="confirmation-section">
                  <div className="confirmation-warning">
                    <h5>⚠️ {t('cleanup.confirmationTitle')}</h5>
                    <p>{t('cleanup.confirmationText')}</p>
                  </div>
                  
                  <div className="confirmation-input">
                    <label htmlFor="confirm-text">
                      {t('cleanup.typeConfirm')}
                    </label>
                    <input
                      id="confirm-text"
                      type="text"
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                      placeholder="DELETE"
                      disabled={isCleaning}
                    />
                  </div>

                  <div className="confirmation-actions">
                    <button
                      className="cancel-btn"
                      onClick={() => {
                        setShowConfirm(false);
                        setConfirmText('');
                      }}
                      disabled={isCleaning}
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      className="confirm-cleanup-btn"
                      onClick={handleCleanup}
                      disabled={!canConfirm || isCleaning}
                    >
                      {isCleaning ? t('cleanup.cleaning') : t('cleanup.confirmCleanup')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="preview-error">
              {preview.error}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SoftDeletedPlayerCleanup;

